import React from 'react';

/* ═══════════════════ Reusable IDE chrome pieces ═══════════════════════ */

/** File-tab bar shown above every code panel */
export const FileTab = ({ name, active = true }: { name: string; active?: boolean }) => (
  <div className="flex items-center border-b border-white/[0.06]" style={{ background: '#181825' }}>
    <div
      className={`flex items-center gap-2 px-3 py-1.5 border-r border-white/[0.06] text-[11px] ${active ? 'border-b-2 border-b-blue-400/60 text-gray-300' : 'text-gray-500'
        }`}
      style={{ background: active ? '#1e1e2e' : 'transparent' }}
    >
      <svg className="w-3 h-3 text-blue-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      {name}
      {active && <span className="text-gray-600 text-[11px] ml-1 cursor-default">&times;</span>}
    </div>
  </div>
);

/** Dark code panel with line numbers */
export const CodePanel = ({
  fileName,
  lines,
  caption,
  highlightSet,
}: {
  fileName: string;
  lines: React.ReactNode[];
  caption?: string;
  highlightSet?: Set<number>;
}) => (
  <div>
    <div
      className="rounded-lg border border-gray-200/80 overflow-hidden shadow-lg shadow-black/10 font-mono text-[13px] leading-[1.6]"
      style={{ background: '#1e1e2e' }}
    >
      <FileTab name={fileName} />
      <div className="flex py-3">
        <div
          className="flex flex-col pr-4 pl-3 select-none flex-shrink-0 text-[12px] leading-[1.6] text-right"
          style={{ color: 'rgba(133,133,133,0.4)' }}
        >
          {lines.map((_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>
        <div className="text-[12px] leading-[1.6] text-gray-300/90 pr-4 whitespace-pre overflow-x-auto">
          {lines.map((line, i) => (
            <div
              key={i}
              className="min-h-[1.6em] -mx-1 px-1"
              style={{
                background: highlightSet?.has(i) ? 'rgba(111,179,242,0.06)' : 'transparent',
              }}
            >
              {line}
            </div>
          ))}
        </div>
      </div>
    </div>
    {caption && <p className="text-[10px] text-gray-500 mt-2 font-mono">{caption}</p>}
  </div>
);

/** Console output panel matching the in-app IDE bottom dock (SVG variant) */
export const ConsolePanel = ({ title, context, lines }: {
  title: string;
  context?: string;
  lines: { time: string; type: 'info' | 'success' | 'warning'; source?: string; message: string }[];
}) => (
  <div
    className="rounded-lg border border-gray-200/80 overflow-hidden shadow-lg shadow-black/10 font-mono text-[12px]"
    style={{ background: '#1e1e2e' }}
  >
    {/* Console header */}
    <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06]" style={{ background: '#181825' }}>
      <div className="flex items-center gap-2 text-[10px]">
        <svg className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
        <span className="text-gray-400">{title}</span>
        {context && (
          <>
            <span className="text-gray-600">&middot;</span>
            <span className="text-gray-500">{context}</span>
          </>
        )}
      </div>
      <span className="text-[9px] bg-gray-600/30 text-gray-400 px-1.5 py-0.5 rounded uppercase tracking-wide">Done</span>
    </div>

    {/* Log lines */}
    <div className="px-3 py-2 space-y-0.5">
      {lines.map((l, i) => (
        <div key={i} className="flex items-start gap-2 py-0.5">
          <span className="text-gray-600 select-none">[{l.time}]</span>
          {l.type === 'success' && <span className="text-emerald-400 mt-px">&#10003;</span>}
          {l.type === 'warning' && <span className="text-amber-400 mt-px">&#9888;</span>}
          {l.type === 'info' && <span className="text-cyan-400 mt-px">&#9432;</span>}
          {l.source && <span className="text-gray-500">[{l.source}]</span>}
          <span className={l.type === 'success' ? 'text-emerald-300' : l.type === 'warning' ? 'text-amber-300' : 'text-gray-300'}>
            {l.message}
          </span>
        </div>
      ))}
    </div>
  </div>
);
